'use client'

import { useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { MapPinIcon, InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { getGeocode, getLatLng } from 'use-places-autocomplete'

import { useBooking } from '../../model/bookingContext'
import { PatientLocation, BookingStepEnum } from '../../model/bookingTypes'
import { BookingStep } from './BookingStep'
import { Summary } from './Summary'
import { IncompletedStepLabel } from './IncompletedStepLabel'
import PlacesAutocomplete from '@/components/ui/comboboxes/PlacesAutocomplete'

export function LocationStep() {
  const { state, goToStep, isStepCompleted } = useBooking()
  const isCompleted = isStepCompleted(BookingStepEnum.LOCATION)

  if (state.currentStep === BookingStepEnum.LOCATION) {
    return (
      <BookingStep>
        <BookingStep.Title>
          <Trans>Your location</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <LocationForm />
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    const text = state.patientLocation?.declined
      ? t`I prefer not to provide my location`
      : [state.patientLocation?.postalCode, state.patientLocation?.city].filter(Boolean).join(' ')

    return <Summary icon="📍" label={t`Your location`} onEdit={() => goToStep(BookingStepEnum.LOCATION)} text={text} />
  }

  return (
    <IncompletedStepLabel icon="📍">
      <Trans>Your location</Trans>
    </IncompletedStepLabel>
  )
}

function LocationForm() {
  const { state, dispatch } = useBooking()

  const [location, setLocation] = useState<PatientLocation>({
    postalCode: state.patientLocation?.postalCode ?? null,
    city: state.patientLocation?.city ?? null,
    coordinates: state.patientLocation?.coordinates ?? null,
    declined: state.patientLocation?.declined ?? false,
  })

  const [isLocating, setIsLocating] = useState(false)
  const [hasAgreedToTerritoriality, setHasAgreedToTerritoriality] = useState(false)

  const geolocationUnsupportedMessage = t`Geolocation is not supported by your browser`
  const geolocationUnavailableMessage = t`Unable to retrieve your location`

  const handlePlaceSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    try {
      const results = await getGeocode({ placeId: prediction.place_id })
      const { lat, lng } = await getLatLng(results[0])

      // Extract postal code and city
      let postalCode = null
      let city = null

      for (const component of results[0].address_components) {
        if (component.types.includes('postal_code')) {
          postalCode = component.long_name
        }
        if (component.types.includes('locality')) {
          city = component.long_name
        }
      }

      setLocation({
        postalCode,
        city,
        coordinates: { lat, lng },
        declined: false,
      })
    } catch (error) {
      console.error('Error getting geocode:', error)
    }
  }

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert(geolocationUnsupportedMessage)
      return
    }

    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords

        try {
          const results = await getGeocode({ location: { lat: latitude, lng: longitude } })

          let postalCode = null
          let city = null

          for (const component of results[0].address_components) {
            if (component.types.includes('postal_code')) {
              postalCode = component.long_name
            }
            if (component.types.includes('locality')) {
              city = component.long_name
            }
          }

          setLocation({
            postalCode,
            city,
            coordinates: { lat: latitude, lng: longitude },
            declined: false,
          })
        } catch (error) {
          console.error('Error getting geocode from coordinates:', error)
        } finally {
          setIsLocating(false)
        }
      },
      (error) => {
        console.error('Geolocation error:', error)
        setIsLocating(false)
        alert(geolocationUnavailableMessage)
      }
    )
  }

  const handleDecline = (checked: boolean) => {
    setLocation((prev) => ({
      ...prev,
      declined: checked,
      postalCode: checked ? null : prev.postalCode,
      city: checked ? null : prev.city,
      coordinates: checked ? null : prev.coordinates,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!hasAgreedToTerritoriality) return
    if (!location.declined && !location.postalCode) return

    dispatch({
      type: 'SET_PATIENT_LOCATION',
      payload: location,
      goToNextStep: true,
    })
  }

  const isFormValid = hasAgreedToTerritoriality && (location.declined || !!location.postalCode)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Territoriality Information Message (Mandatory) */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
        <div className="flex items-start space-x-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="space-y-3 text-sm text-blue-800">
            <p>
              <Trans>
                Votre localisation vous est demandée afin de vous mettre prioritairement en relation avec un médecin proche de chez vous. Cette proximité doit
                permettre d’optimiser la qualité de votre prise en charge. Dans tous les cas, le médecin évaluera et facilitera le renvoi vers une consultation
                en présentiel si votre état de santé le nécessite.
              </Trans>
            </p>
            <p data-testid="central-waiting-room-explanation">
              <Trans>
                Dans notre parcours, vous êtes orienté vers une salle d’attente centrale : vous ne choisissez pas directement un médecin. Votre localisation peut
                être utilisée par l’équipe de régulation ou par le système d’orientation afin de faciliter une prise en charge de proximité lorsque l’organisation
                médicale disponible le permet.
              </Trans>
            </p>
            <p>
              <Trans>Pour en savoir plus sur les conditions de prise en charge des téléconsultations par l’Assurance Maladie :</Trans>{' '}
              <a
                className="font-medium underline"
                href="https://www.ameli.fr/assure/remboursements/rembourse/consultations-telemedecine/telemedecine/teleconsultation"
                rel="noopener noreferrer"
                target="_blank"
              >
                ameli.fr
              </a>
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <input
            id="territorialityAgreement"
            data-testid="territoriality-agreement"
            type="checkbox"
            checked={hasAgreedToTerritoriality}
            onChange={(e) => setHasAgreedToTerritoriality(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label htmlFor="territorialityAgreement" className="text-sm font-medium text-blue-900">
            <Trans>J’ai lu et compris les informations relatives à la territorialité et à l’utilisation de ma localisation.</Trans>
          </label>
        </div>
      </div>

      {!location.declined ? (
        <div className="space-y-4">
          <PlacesAutocomplete
            label={t`Enter your postal code or city`}
            onSelect={handlePlaceSelect}
            defaultValue={location.city ? `${location.postalCode} ${location.city}` : undefined}
          />

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={isLocating}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
            >
              {isLocating ? <span className="animate-spin mr-2">⏳</span> : <MapPinIcon className="h-5 w-5 mr-2 text-gray-400" />}
              <Trans>Use my location</Trans>
            </button>
          </div>

          {location.postalCode && (
            <div className="text-sm text-green-600 font-medium flex items-center">
              <Trans>Location saved</Trans>: {location.postalCode} {location.city}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
          <p className="text-sm text-yellow-800">
            <Trans>
              Sans votre localisation, l’orientation ne pourra pas tenir compte de votre territoire. Vous serez tout de même orienté vers la salle d’attente
              centrale.
            </Trans>
          </p>
        </div>
      )}

      <div className="flex items-center space-x-3 pt-2">
        <input
          id="declineLocation"
          data-testid="decline-location"
          type="checkbox"
          checked={location.declined}
          onChange={(e) => handleDecline(e.target.checked)}
          className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
        />
        <label htmlFor="declineLocation" className="text-sm text-gray-700">
          <Trans>I prefer not to provide my location</Trans>
        </label>
      </div>

      {/* Submit button */}
      <div className="flex justify-end sm:absolute sm:top-full sm:right-0 sm:!m-0">
        <button
          className="inline-flex flex-1 items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-300 sm:rounded-none"
          disabled={!isFormValid}
          type="submit"
          data-testid="location-continue"
        >
          <Trans>Continue</Trans>
        </button>
      </div>
    </form>
  )
}
